/**
 * Sorteio por dias específicos (item 3 do pedido de 19/10).
 *
 * Os dias são texto 'AAAA-MM-DD' (dia civil de Brasília, o backend filtra pela data em
 * America/Sao_Paulo). Toda conta de calendário aqui usa Date.UTC, para não depender do fuso do
 * aparelho do Master. Limite de 31 dias marcados por sorteio (decisão registrada).
 */
export const LIMITE_DIAS = 31;

const DIA = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n) => String(n).padStart(2, "0");

// "todos" = artigo do atalho ("todos os sábados", "todas as segundas-feiras").
export const DIAS_SEMANA = [
  { valor: 0, nome: "domingo", plural: "domingos", todos: "todos os", curto: "Dom" },
  { valor: 1, nome: "segunda-feira", plural: "segundas-feiras", todos: "todas as", curto: "Seg" },
  { valor: 2, nome: "terça-feira", plural: "terças-feiras", todos: "todas as", curto: "Ter" },
  { valor: 3, nome: "quarta-feira", plural: "quartas-feiras", todos: "todas as", curto: "Qua" },
  { valor: 4, nome: "quinta-feira", plural: "quintas-feiras", todos: "todas as", curto: "Qui" },
  { valor: 5, nome: "sexta-feira", plural: "sextas-feiras", todos: "todas as", curto: "Sex" },
  { valor: 6, nome: "sábado", plural: "sábados", todos: "todos os", curto: "Sáb" },
];

export const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** 'AAAA-MM-DD' que existe no calendário (2026-02-30 não existe). */
export function diaValido(s) {
  if (!DIA.test(String(s || ""))) return false;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** Sem repetidos, só dias válidos, em ordem crescente. */
export function normalizarDias(lista) {
  return [...new Set((lista || []).filter(diaValido))].sort();
}

/** 'AAAA-MM' do dia (ou do mês informado). */
export function mesDoDia(dia) {
  return String(dia || "").slice(0, 7);
}

/** Soma meses a 'AAAA-MM' (negativo volta). */
export function somaMes(mes, n) {
  const [y, m] = String(mes).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}`;
}

/** "outubro/2026" */
export function nomeMes(mes) {
  const [y, m] = String(mes).split("-").map(Number);
  return `${MESES[m - 1] || ""}/${y}`;
}

/** Todos os dias do mês 'AAAA-MM' que caem no dia da semana (0 = domingo ... 6 = sábado). */
export function diasDaSemanaNoMes(mes, diaSemana) {
  const [y, m] = String(mes).split("-").map(Number);
  const out = [];
  if (!y || !m) return out;
  for (let d = 1; d <= 31; d += 1) {
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (dt.getUTCMonth() !== m - 1) break;
    if (dt.getUTCDay() === Number(diaSemana)) out.push(`${y}-${pad(m)}-${pad(d)}`);
  }
  return out;
}

/**
 * Semanas do mês para a grade do seletor: 7 colunas (domingo a sábado), null fora do mês.
 */
export function gradeDoMes(mes) {
  const [y, m] = String(mes).split("-").map(Number);
  const primeiro = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const celulas = Array(primeiro).fill(null);
  for (let d = 1; d <= total; d += 1) celulas.push(`${y}-${pad(m)}-${pad(d)}`);
  while (celulas.length % 7) celulas.push(null);
  const semanas = [];
  for (let i = 0; i < celulas.length; i += 7) semanas.push(celulas.slice(i, i + 7));
  return semanas;
}

/** Marca/desmarca um dia. Passar do limite devolve a lista como estava + erro. */
export function alternarDia(lista, dia, limite = LIMITE_DIAS) {
  const atual = normalizarDias(lista);
  if (!diaValido(dia)) return { dias: atual, erro: null };
  if (atual.includes(dia)) return { dias: atual.filter((d) => d !== dia), erro: null };
  if (atual.length >= limite) {
    return { dias: atual, erro: `O sorteio aceita no máximo ${limite} dias marcados.` };
  }
  return { dias: normalizarDias([...atual, dia]), erro: null };
}

/** Acrescenta vários dias (atalho). Passar do limite não aplica nada + erro. */
export function unirDias(lista, novos, limite = LIMITE_DIAS) {
  const uniao = normalizarDias([...(lista || []), ...(novos || [])]);
  if (uniao.length > limite) {
    return { dias: normalizarDias(lista), erro: `O sorteio aceita no máximo ${limite} dias marcados (ficariam ${uniao.length}).` };
  }
  return { dias: uniao, erro: null };
}

/** '2026-10-04' -> '04/10' */
export function fmtDiaCurto(dia) {
  if (!diaValido(dia)) return String(dia || "");
  return `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
}

/** '2026-10-04' -> '04/10/2026' */
export function fmtDiaBR(dia) {
  if (!diaValido(dia)) return String(dia || "");
  return `${dia.slice(8, 10)}/${dia.slice(5, 7)}/${dia.slice(0, 4)}`;
}

/** "04/10, 11/10, 18/10 e mais 2" (resumo para confirmação e histórico). */
export function resumoDias(dias, max = 6) {
  const lista = normalizarDias(dias);
  if (!lista.length) return "";
  const mostra = lista.slice(0, max).map(fmtDiaCurto).join(", ");
  const resto = lista.length - max;
  return resto > 0 ? `${mostra} e mais ${resto}` : mostra;
}
